import { useState, useEffect } from 'react';
import { SiteImages } from '@/types/site.types';
import { getImageUrl } from '@/utils/imageHelper';

export interface ImageWithUrl {
    imageUrl: string;
    loadedUrl: string | null;
    loading: boolean;
    error: boolean;
}

export interface UseImageLoaderReturn {
    imagesWithUrls: ImageWithUrl[];
    isLoadingImages: boolean;
}

/**
 * Custom hook to load image URLs from Firebase Storage
 * @param images - Array of site images to load
 * @returns Object with images array and loading state
 */
export const useImageLoader = (images: SiteImages[]): UseImageLoaderReturn => {
    const [imagesWithUrls, setImagesWithUrls] = useState<ImageWithUrl[]>([]);
    const [isLoadingImages, setIsLoadingImages] = useState(false);

    useEffect(() => {
        if (!images || images.length === 0) {
            setImagesWithUrls([]);
            setIsLoadingImages(false);
            return;
        }

        const loadImageUrls = async () => {
            setIsLoadingImages(true);
            // Initialize with loading state
            const initialState = images.map(img => ({
                imageUrl: img.imageUrl,
                loadedUrl: null,
                loading: true,
                error: false
            }));
            setImagesWithUrls(initialState);

            // Load all image URLs in parallel
            const urlPromises = images.map(async (image, index) => {
                try {
                    const url = await getImageUrl(image.imageUrl);
                    return {
                        index,
                        imageUrl: image.imageUrl,
                        loadedUrl: url,
                        loading: false,
                        error: !url
                    };
                } catch (error) {
                    console.error(`Error loading image ${image.imageUrl}:`, error);
                    return {
                        index,
                        imageUrl: image.imageUrl,
                        loadedUrl: null,
                        loading: false,
                        error: true
                    };
                }
            });

            const results = await Promise.all(urlPromises);

            // Update state with loaded URLs
            setImagesWithUrls(prev => {
                const newState = [...prev];
                results.forEach(result => {
                    newState[result.index] = result;
                });
                return newState;
            });
            setIsLoadingImages(false);
        };

        loadImageUrls();
    }, [images]);

    return { imagesWithUrls, isLoadingImages };
};
